import { useEffect, useState } from "react";
import * as api from "../../lib/api.ts";
import { retryDelayMs } from "./retry.ts";

/**
 * Адреса содержимого файлов, с кэшем на вкладку.
 *
 * Кэш здесь не микрооптимизация, а условие работы предзагрузки. Каждый вызов
 * `POST /link` выдаёт НОВУЮ подпись, то есть новый адрес, а браузер кэширует по
 * адресу. Прогреть кэш одним адресом и играть по другому значит скачать файл
 * дважды, и второй раз ровно тогда, когда все смотрят в экран. Поэтому ссылка
 * на файл берётся один раз и переиспользуется, пока не подходит к концу срок.
 *
 * Отдача помечена `private, max-age` ровно до истечения подписи (см.
 * routes/media.ts), так что кэш браузера и срок доступа кончаются вместе.
 */

interface Entry {
  url: string;
  expiresAt: number;
}

/** За сколько до истечения просим новую. Меньше минуты плееру уже мало. */
const REFRESH_MARGIN_MS = 60_000;

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<Entry>>();

function fresh(entry: Entry | undefined): entry is Entry {
  return entry !== undefined && entry.expiresAt - Date.now() > REFRESH_MARGIN_MS;
}

/**
 * Адрес содержимого. Бросает `ApiError`: `file_deleted` это «файла больше
 * нет», всё остальное это отказ или поломка сети.
 */
export async function mediaUrl(fileId: string, opts?: { force?: boolean }): Promise<string> {
  if (opts?.force === true) {
    cache.delete(fileId);
    inflight.delete(fileId);
  }

  const cached = cache.get(fileId);
  if (fresh(cached)) return cached.url;

  // Один запрос на файл, даже если ссылку одновременно спросили плеер и
  // предзагрузка соседнего слайда
  let pending = inflight.get(fileId);
  if (pending === undefined) {
    pending = api
      .mediaFileUrl(fileId)
      .then((link) => {
        const entry: Entry = { url: link.url, expiresAt: link.expiresAt };
        cache.set(fileId, entry);
        return entry;
      })
      .finally(() => inflight.delete(fileId));
    inflight.set(fileId, pending);
  }

  return (await pending).url;
}

/** Забыть выданный адрес: следующий запрос выпустит подпись заново. */
export function forgetMediaUrl(fileId: string): void {
  cache.delete(fileId);
}

export type MediaUrlStatus = "idle" | "loading" | "ready" | "gone" | "denied" | "error";

export interface MediaUrlView {
  url: string | null;
  status: MediaUrlStatus;
}

const IDLE: MediaUrlView = { url: null, status: "idle" };

/**
 * Адрес файла для React.
 *
 * `attempt` это номер попытки: его смена запрашивает адрес заново. Нужен там,
 * где картинка не загрузилась уже ПО выданному адресу (подпись отвергнута,
 * файл ушёл в корзину после выдачи): перед сменой номера зовущий забывает
 * адрес (`forgetMediaUrl`), иначе кэш вернул бы тот же.
 *
 * Отдельно различаем «файла больше нет» и «не отдали»: на слайде первое это
 * честная надпись «видео удалено преподавателем», а второе повод показать, что
 * что-то не так с доступом. Ломать плеер и сыпать ошибками в консоль нельзя ни
 * в том, ни в другом случае, поэтому `src` без адреса не выставляется вовсе.
 *
 * `retry`: статус `error` (сеть, сервер) повторяется сам с паузой
 * `retryDelayMs(n)` после n-й неудачи, пока компонент смонтирован; `gone` и
 * `denied` не повторяются, это ответ, а не поломка. Включён только у медиа
 * слайдов (решение владельца 02.11.2026, раздел 11
 * `docs/slide-media-sync-plan.md`): там ученик без него сидит перед «Файл
 * недоступен» до перезагрузки страницы. Остальные потребители живут как
 * раньше.
 */
export function useMediaUrl(
  fileId: string | null,
  attempt = 0,
  opts?: { retry?: boolean },
): MediaUrlView {
  const [view, setView] = useState<MediaUrlView>(IDLE);
  const retry = opts?.retry === true;

  useEffect(() => {
    if (fileId === null) {
      setView(IDLE);
      return;
    }

    let alive = true;
    let failures = 0;
    let timer: number | undefined;
    setView((prev) => (prev.status === "loading" ? prev : { url: null, status: "loading" }));

    const request = () => {
      void mediaUrl(fileId)
        .then((url) => {
          if (alive) setView({ url, status: "ready" });
        })
        .catch((err: unknown) => {
          if (!alive) return;
          const code = err instanceof api.ApiError ? err.code : "";
          const status =
            code === "file_deleted" ? "gone" : code === "file_not_found" ? "denied" : "error";
          setView({ url: null, status });
          if (!retry || status !== "error") return;
          failures += 1;
          timer = window.setTimeout(request, retryDelayMs(failures));
        });
    };
    request();

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [fileId, attempt, retry]);

  return view;
}
